/*======================================================================================================================
  * Bluebell: src/app/guards/eula-guard/eula.guard.ts
  * Last Edited: 11/06/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { EulaLatestStateResponse, EulaAcceptedData } from '@app/pages/eula/eula.interfaces';
import { Store } from '@ngrx/store';
import { ApiService } from '@state/api.service';
import { AuthenticationService } from '@state/authentication/authentication.service';
import { refreshAccessTokenSuccess } from '@state/authentication/authentication.actions';
import { TokenResponse } from '@state/authentication/authentication.interfaces';
import { mergeMap, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Require the User to have signed the active EULA, otherwise redirect them to
 * a page where they are forced to sign it, or logout.
 */
export const eulaGuard: CanActivateFn = (_: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const api = inject(ApiService);
  const router = inject(Router);
  const oauth = inject(AuthenticationService);
  const store = inject(Store);

  const onSuccessfulResponse = ({ eula, updated, signed_active }: EulaAcceptedData): boolean | UrlTree =>
    signed_active || router.createUrlTree(['/eula', eula], { queryParams: { updated, next: state.url } });


  const onErrorResponse = (): Observable<UrlTree> => of(router.parseUrl('/logout'));

  return api.eulaLatestState().pipe(
    map(({ data }: EulaLatestStateResponse) => onSuccessfulResponse(data)),
    catchError((errorResponse: HttpErrorResponse) => {
      const { status } = errorResponse;
      if (status === 401) {
        return oauth.refreshLogin().pipe(
          mergeMap((tokenResponse: TokenResponse) => {
            store.dispatch(refreshAccessTokenSuccess(tokenResponse));
            return api.eulaLatestState().pipe(
              map(({ data }: EulaLatestStateResponse) => onSuccessfulResponse(data)),
              catchError(() => onErrorResponse())
            );
          }),
          catchError(() => onErrorResponse())
        );
      } else {
        return onErrorResponse();
      }
    })
  );
};
